import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import _ from 'underscore';
import { humanize, titleize } from 'underscore.string';

import OrderShipmentsStore from 'stores/OrderShipmentsStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import EditEquipmentModal from 'components/modules/equipment/EditEquipmentModal';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import {
  TableRow,
  TableCell,
  BadgeAlt,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { MoreHorizontal } from 'lucide-react';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import { createShipment } from '../CreateShipment';
import RemoveEquipmentModal from './RemoveEquipmentModal';
import { sluggify } from 'utils/localization/utils';
import { isLegManagementEnabled } from 'helpers/legs-helper';

const EquipmentDateField = ({ label, date, dataTestId }) => {
  if (!date) {
    return null;
  }

  return (
    <div className="text-grey-darker mb-2">
      <span className="mr-2 font-bold">{label}:</span>
      <span data-testid={dataTestId}>{moment(date).format('L')}</span>
    </div>
  );
};

EquipmentDateField.propTypes = {
  label: PropTypes.string,
  date: PropTypes.string,
};

const isBooking = ({ equipment, order, shipments }) => {
  if (
    _.findWhere(order.shipments_staging, {
      equipment_id: equipment.equipment_id,
    })
  ) {
    return true;
  }
  for (let i = 0; i < shipments.length; i += 1) {
    const shipment = shipments[i];
    if (!shipment) return false;
  }
  return false;
};

const shipmentsHaveEquipment = (id, shipments) => {
  return (
    shipments.filter((shipment) => {
      const { stops } = shipment;

      if (shipment.canceled) {
        return false;
      }

      // aggregate all the equipment IDs for all stops
      // check if the list includes the target equipmentId
      return stops
        .reduce((shipmentEquipments, stop) => {
          const equipmentIds = (stop.equipments || []).map((e) => e._id);
          return shipmentEquipments.concat(equipmentIds);
        }, [])
        .includes(id);
    }).length > 0
  );
};

const shipmentExists = (props) => {
  const {
    equipment: { shipment },
  } = props;
  const staging = props.order.shipments_staging;
  const id = props.equipment._id;

  return !!shipment || shipmentsHaveEquipment(id, staging);
};

class EquipmentItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showConfirmModal: false,
      showRemoveModal: false,
      isExpanded: false,
      showEditEquipmentModal: false,
      disableCreateShipment: false,
    };
  }

  componentDidMount() {
    OrderShipmentsStore.addShipmentCreationListener(
      this.handleOrderShipmentCreation,
    );
  }

  componentWillUnmount() {
    OrderShipmentsStore.removeShipmentCreationListener(
      this.handleOrderShipmentCreation,
    );
  }

  handleOrderShipmentCreation = (type) => {
    // Can't create a shipment while a request is in progress. When it's done the type will be 'SUCCEEDED' or 'FAILED'.
    const disableCreateShipment = type === 'REQUESTED';
    this.setState({ disableCreateShipment });
  };

  handleRemoveEquipment = () => this.setState({ showRemoveModal: true });

  toggleEditing = () => this.setState({ isExpanded: !this.state.isExpanded });

  saveEditEquipment = (equipment) => {
    EquipmentActionCreators.updateEquipment(equipment);
  };

  handleManageEquipment = () => {
    this.setState({ showEditEquipmentModal: true });
  };

  handleCreateShipment = (eq) => {
    createShipment(this.props, {
      shipment_type: this.state.shipment_type,
      equipment: eq,
    });
    this.setState({ showConfirmModal: false });
  };

  hasShipments() {
    const {
      equipment: { shipment },
    } = this.props;

    return !!shipment;
  }

  renderDates() {
    const {
      equipment: {
        earliest_return_date,
        last_free_date,
        earliest_empty_date,
        last_cut_date,
      },
      t,
    } = this.props;

    return (
      <div>
        <EquipmentDateField
          date={earliest_return_date}
          label={t('EARLIEST_RETURN_DATE')}
          dataTestId="earliest-return-date"
        />
        <EquipmentDateField
          date={last_free_date}
          label="Last Free Date"
          dataTestId="last-free-date"
        />
        <EquipmentDateField
          date={earliest_empty_date}
          label={t('EARLIEST_DATE_TO_GET_EMPTY')}
          dataTestId="earliest-date-to-get-empty"
        />
        <EquipmentDateField
          date={last_cut_date}
          label={t('LAST_CUT_DATE')}
          dataTestId="last-cut-date"
        />
      </div>
    );
  }

  renderStagedBadge() {
    const {
      equipment,
      order: { shipments_staging },
      t,
    } = this.props;

    const equipmentInStaged = shipments_staging.some(
      (s) => s.equipments && s.equipments.find((e) => e._id === equipment._id),
    );

    if (!equipmentInStaged) {
      return null;
    }

    return (
      <BadgeAlt
        variant="outline"
        className="bg-gray-100 text-gray-800 border-gray-300 whitespace-nowrap"
      >
        {t('STAGED')}
      </BadgeAlt>
    );
  }

  renderStatus() {
    const {
      equipment: { status },
      t,
    } = this.props;

    if (!status) return null;

    const translatedStatus = t(sluggify(status));
    if (!translatedStatus || translatedStatus === 'N/A') return null;

    return (
      <span data-testid="equipment-status">
        <ShipmentStatusBadge status={status} label={translatedStatus} />
      </span>
    );
  }

  renderIndicator() {
    if (!this.hasShipments()) {
      return null;
    }

    const {
      equipment: { shipment },
    } = this.props;
    return (
      <BadgeAlt
        variant="outline"
        className="bg-green-100 text-green-800 border-green-300 whitespace-nowrap"
      >
        {shipment.shipment_id}
      </BadgeAlt>
    );
  }

  render() {
    const { order, equipment, shipments, t } = this.props;
    const eq = { _id: equipment._id, equipment_id: equipment.equipment_id };

    return (
      <TableRow>
        <TableCell className="whitespace-nowrap">
          <button onClick={() => this.handleManageEquipment()}>
            <span className="text-blue-dark" data-testid="equipment-id">
              {equipment.equipment_id}
            </span>
          </button>
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="equipment-type">
          {humanize(equipment._type)}
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="equipment-size">
          {equipment._equipmentSize}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {this.renderDates()}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <div className="flex items-center">
            {this.renderStatus()}
            {this.renderStagedBadge()}
            {this.renderIndicator()}
          </div>
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => this.handleManageEquipment()}>
                {t('EDIT_DETAILS')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={
                  this.state.disableCreateShipment ||
                  isLegManagementEnabled(this.props.order)
                }
                onClick={() => {
                  if (shipmentExists(this.props)) {
                    this.setState({
                      showConfirmModal: true,
                      shipment_type: 'drayage',
                    });
                  } else {
                    createShipment(this.props, {
                      shipment_type: 'drayage',
                      equipment: eq,
                    });
                  }
                }}
              >
                {t('CREATE_DRAY_SHIPMENT')}
              </DropdownMenuItem>
              {this.props.order.mode === 'intermodal' && (
                <DropdownMenuItem
                  disabled={
                    this.state.disableCreateShipment ||
                    isLegManagementEnabled(this.props.order)
                  }
                  onClick={() => {
                    if (shipmentExists(this.props)) {
                      this.setState({
                        showConfirmModal: true,
                        shipment_type: 'rail',
                      });
                    } else {
                      createShipment(this.props, {
                        shipment_type: 'rail',
                        equipment: eq,
                      });
                    }
                  }}
                >
                  {t('CREATE_RAIL_SHIPMENT')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={
                  isBooking(this.props) ||
                  shipmentsHaveEquipment(equipment._id, shipments)
                }
                onClick={() => this.handleRemoveEquipment()}
              >
                {titleize(t('REMOVE'))}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {this.state.showEditEquipmentModal && (
            <EditEquipmentModal
              show={this.state.showEditEquipmentModal}
              onHide={() => this.setState({ showEditEquipmentModal: false })}
              order={order}
              equipment={equipment}
              onSave={(eq) => this.saveEditEquipment(eq)}
            />
          )}
          {this.state.showRemoveModal && (
            <RemoveEquipmentModal
              show={this.state.showRemoveModal}
              onClose={() => this.setState({ showRemoveModal: false })}
              orderId={order._id}
              equipmentId={equipment.equipment_id}
              _id={equipment._id}
            />
          )}
          {this.state.showConfirmModal && (
            <ConfirmModal
              show={this.state.showConfirmModal}
              title={t('SHIPMENT_CONFIRMATION')}
              message={t('SHIPMENT_CONTAINS_EQUIPMENT_WARNING')}
              onConfirm={() => this.handleCreateShipment(eq)}
              onHide={() => this.setState({ showConfirmModal: false })}
            />
          )}
        </TableCell>
      </TableRow>
    );
  }
}

EquipmentItem.propTypes = {
  order: PropTypes.object,
  equipment: PropTypes.object,
};

export default withTranslation()(EquipmentItem);
